import type { LocalDate } from '../../db/types';

const localDatePattern = /^\d{4}-\d{2}-\d{2}$/;

export function toLocalDate(date: Date): LocalDate {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseLocalDate(value: LocalDate): Date {
  if (!localDatePattern.test(value)) throw new Error('Некорректная календарная дата.');
  const [yearText, monthText, dayText] = value.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error('Некорректная календарная дата.');
  }
  return date;
}

export function addLocalDays(value: LocalDate, days: number): LocalDate {
  const date = parseLocalDate(value);
  date.setDate(date.getDate() + days);
  return toLocalDate(date);
}

export function compareLocalDates(left: LocalDate, right: LocalDate): number {
  return left.localeCompare(right);
}

export function formatLocalDate(value: LocalDate, options?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('ru-RU', options ?? { day: 'numeric', month: 'long', year: 'numeric' })
    .format(parseLocalDate(value));
}

export function weekdayShort(value: LocalDate): string {
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'short' }).format(parseLocalDate(value)).replace('.', '');
}

export function dayNumber(value: LocalDate): string {
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric' }).format(parseLocalDate(value));
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function isIsoDateTime(value: string): boolean {
  return Number.isFinite(Date.parse(value)) && value.includes('T');
}

export function dateTimeToLocalDate(value: string): LocalDate {
  return toLocalDate(new Date(value));
}

export function durationSeconds(startedAt: string | null, finishedAt: string | null, now = Date.now()): number {
  if (!startedAt) return 0;
  return Math.max(0, Math.round(((finishedAt ? Date.parse(finishedAt) : now) - Date.parse(startedAt)) / 1000));
}

export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

export function isValidTime(value: string): boolean {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

export function combineLocalDateAndTime(date: LocalDate, time: string): Date {
  if (!isValidTime(time)) throw new Error('Некорректное время.');
  const result = parseLocalDate(date);
  const [hour, minute] = time.split(':').map(Number);
  result.setHours(hour ?? 0, minute ?? 0, 0, 0);
  return result;
}

export function daysSince(value: string, now = Date.now()): number {
  return Math.floor((now - Date.parse(value)) / 86_400_000);
}
